import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import type { ServiceStoreType } from "../../../types/ServiceStoreTypes";
import { findOrThrow, loadErrorMessage } from "shared/loadOrError";
import type {
  ClientInstance,
  ClientInstanceStatusFilter,
} from "../infrastructure.types";
import {
  isClientInstanceMissing,
  isClientInstanceRunning,
  isInternalInstance,
} from "../infrastructure.types";
import type { InstanceActionType } from "../infrastructure.types";
import { canManageInfra } from "../rbac";
import { runInstanceAction } from "../instanceActions";
import {
  dateValue,
  nextSort,
  sortRows,
  textValue,
  type SortDir,
  type SortValue,
} from "components/TableSort/tableSort";
import { followHandOff } from "../InstanceActions/InstanceActionsStore";
import { isSessionEnded, sessionMark } from "stores/sessionGuard";

export type InternalContainerSortKey =
  | "containerName"
  | "dockerHostName"
  | "imageRef"
  | "status"
  | "lastSeenAt";

const DESC_FIRST: ReadonlySet<InternalContainerSortKey> = new Set(["lastSeenAt"]);

/**
 * Route ViewStore for Internal Containers — IntraSync's OWN infrastructure
 * (HQ itself, agents, harness DBs), operator-designated via the
 * `mark-internal` instance action rather than assigned to a client.
 *
 * Rows come from the same live `client-instances` view the fleet grid uses:
 * internal containers are unlabeled in-scope containers, so they only appear
 * in the `includeUnassigned` listing — the reconcile pass stamps their
 * persisted designation back as assignmentState 'internal', which is what
 * this store filters on. Update tracking (registry sweep) covers them like
 * any row with an imageRef, so update badges keep working here.
 */
/**
 * The status a lifecycle action is heading towards, for the optimistic flip.
 *
 * Deploy, rollback and update-to-latest are deliberately absent: they replace
 * the image and the resulting state is the server's to report, so guessing one
 * would show a status that never existed.
 */
const optimisticStatusFor = (action: InstanceActionType): string | null => {
  if (action === "start") return "starting";
  if (action === "stop") return "stopping";
  if (action === "restart") return "restarting";
  return null;
};

class InternalContainersStore {
  rootStore: RootStore;

  loading = false;
  error: string | null = null;
  actioningIds: string[] = [];

  searchQuery = "";
  statusFilter: ClientInstanceStatusFilter = "all";
  sortKey: InternalContainerSortKey = "containerName";
  sortDir: SortDir = "asc";

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  private get instancesService(): ServiceStoreType | null {
    return (
      ((this.rootStore.serviceStoreManager?.getStore("client-instances") ??
        this.rootStore.clientInstancesStore) as unknown as ServiceStoreType | null) ??
      null
    );
  }

  private get actionsService(): ServiceStoreType | null {
    return (
      ((this.rootStore.serviceStoreManager?.getStore("instance-actions") ??
        this.rootStore.instanceActionsStore) as unknown as ServiceStoreType | null) ??
      null
    );
  }

  get canManage(): boolean {
    return canManageInfra(this.rootStore);
  }

  /** All internal-designated instances in the shared entity cache. */
  get internalInstances(): ClientInstance[] {
    return (
      (this.instancesService?.entities ?? []) as unknown as ClientInstance[]
    ).filter((i) => isInternalInstance(i));
  }

  get filteredInstances(): ClientInstance[] {
    let result = this.internalInstances;

    if (this.statusFilter === "running") {
      result = result.filter((i) => isClientInstanceRunning(i.status));
    } else if (this.statusFilter === "stopped") {
      result = result.filter((i) => !isClientInstanceRunning(i.status));
    }

    if (this.searchQuery.trim()) {
      const q = this.searchQuery.toLowerCase().trim();
      result = result.filter((i) =>
        [i.containerName, i.imageRef, i.dockerHostName]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(q)
      );
    }
    return result;
  }

  get sortedInstances(): ClientInstance[] {
    const key = this.sortKey;
    const valueOf = (i: ClientInstance): SortValue => {
      switch (key) {
        case "containerName":
          return textValue(i.containerName);
        case "dockerHostName":
          return textValue(i.dockerHostName);
        case "imageRef":
          return textValue(i.imageRef);
        case "status":
          return textValue(i.status);
        case "lastSeenAt":
          return dateValue(i.lastSeenAt);
      }
    };
    return sortRows(this.filteredInstances, valueOf, this.sortDir);
  }

  get runningCount(): number {
    return this.internalInstances.filter((i) => isClientInstanceRunning(i.status)).length;
  }

  get updateAvailableCount(): number {
    return this.internalInstances.filter((i) => !!i.updateAvailable).length;
  }

  isActioning(instanceId: string): boolean {
    return this.actioningIds.includes(instanceId);
  }

  setSearchQuery(q: string) {
    this.searchQuery = q;
  }

  setStatusFilter(filter: ClientInstanceStatusFilter) {
    this.statusFilter = filter;
  }

  setSort(key: InternalContainerSortKey) {
    const next = nextSort(this.sortKey, this.sortDir, key, DESC_FIRST);
    this.sortKey = next.key;
    this.sortDir = next.dir;
  }

  /** Load the live fleet listing. `includeUnassigned` is required: internal
   *  containers are unlabeled, so only the discovery listing surfaces them. */
  async load() {
    this.loading = true;
    this.error = null;
    try {
      // findOrThrow: a swallowed failure read as "No internal containers yet".
      await findOrThrow(this.instancesService, {
        query: { includeUnassigned: true },
      });
    } catch (err: unknown) {
      runInAction(() => {
        if (!isSessionEnded(err)) this.error = loadErrorMessage(err, "Failed to load internal containers");
      });
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  async reload() {
    await this.load();
  }

  /** Release the designation — the container returns to the Unassigned pool in
   *  Client Instances (operator+). */
  /**
   * Remove a MISSING internal row from the cache (operator+, confirm-gated by
   * the route dialog, audited API-side). Same semantics as the Client
   * Instances grid's Remove: the container no longer exists on its host, so
   * this deletes the dead row — Release would only turn it into an unassigned
   * missing row. The API refuses any non-missing row.
   */
  /**
   * Run a container lifecycle action (start / stop / restart) on an internal
   * container, with the same optimistic-status + reload behaviour the Client
   * Instances grid uses.
   *
   * This grid could SEE that an update was available -- `updateAvailableCount`
   * has always counted them -- and offered no way to act on it. Deploy,
   * rollback and update-to-latest all reach the shared `instanceActionsStore`;
   * only the plain lifecycle transport lives here, because the optimistic flip
   * and the reload are this store's cache to manage.
   *
   * These are IntraSync's OWN containers, so a stop here takes an HQ service
   * down rather than a customer's. That is why every one of these routes
   * through the confirmation dialog.
   */
  async runLifecycleAction(
    instance: ClientInstance,
    action: InstanceActionType,
    /** The confirm dialog's progress token (InstanceActionsStore.runConfirmed). */
    progressToken?: string
  ): Promise<boolean> {
    if (!this.canManage) return false;

    // A missing row has no container to act on. The API refuses too; saying so
    // here saves the round-trip and gives the same message.
    if (isClientInstanceMissing(instance.status)) {
      runInAction(() => {
        this.error =
          "This container is missing — it no longer exists on its host. Refresh the list.";
      });
      return false;
    }

    this.actioningIds = [...this.actioningIds, instance.id];
    this.error = null;

    const optimistic = optimisticStatusFor(action);
    if (optimistic) this.patchInstance(instance.id, optimistic);

    const ended = sessionMark(this.rootStore);
    try {
      const result = await runInstanceAction(this.actionsService, {
        action,
        instanceId: instance.id,
        ...(progressToken ? { progressToken } : {}),
      });
      // A restart of the container serving HQ is handed to a helper: follow it.
      followHandOff(this.rootStore, result, instance);
      await this.load();
      return true;
    } catch (err: unknown) {
      // A sign-out ended the call: no message, and no re-pull — it would go
      // out in the next person's session and fill the cache reset() emptied.
      if (isSessionEnded(err) || ended()) return false;
      const message =
        err instanceof Error ? err.message : `Failed to ${action} container`;
      // Re-pull the truth rather than trying to invert the optimistic flip --
      // and set the message AFTER, because `load()` clears `error` on entry.
      // Setting it first looked right and silently swallowed every failure.
      await this.load();
      // load() swallows a sign-out during the re-pull: the message is not the next person's.
      if (ended()) return false;
      runInAction(() => {
        this.error = message;
      });
      console.error(`Internal container ${action} failed:`, err);
      return false;
    } finally {
      runInAction(() => {
        this.actioningIds = this.actioningIds.filter((id) => id !== instance.id);
      });
    }
  }

  /**
   * Flip one cached row's status so the grid reacts before the round-trip
   * lands. Purely cosmetic: `load()` overwrites it either way.
   */
  private patchInstance(instanceId: string, status: string) {
    const svc = this.instancesService;
    const row = (svc?.entities as unknown as ClientInstance[] | undefined)?.find(
      (i) => i.id === instanceId
    );
    if (row) runInAction(() => ((row as { status: string }).status = status));
  }

  async remove(instance: ClientInstance): Promise<boolean> {
    if (!this.canManage) return false;
    this.actioningIds = [...this.actioningIds, instance.id];
    this.error = null;
    try {
      await runInstanceAction(this.actionsService, {
        action: "remove",
        instanceId: instance.id,
      });
      await this.load();
      return true;
    } catch (err: unknown) {
      runInAction(() => {
        if (!isSessionEnded(err)) {
          this.error =
            err instanceof Error ? err.message : "Failed to remove container row";
        }
      });
      return false;
    } finally {
      runInAction(() => {
        this.actioningIds = this.actioningIds.filter((id) => id !== instance.id);
      });
    }
  }

  async release(instance: ClientInstance): Promise<boolean> {
    if (!this.canManage) return false;
    this.actioningIds = [...this.actioningIds, instance.id];
    this.error = null;
    try {
      await runInstanceAction(this.actionsService, {
        action: "unmark-internal",
        instanceId: instance.id,
      });
      await this.load();
      return true;
    } catch (err: unknown) {
      runInAction(() => {
        if (!isSessionEnded(err)) {
          this.error =
            err instanceof Error ? err.message : "Failed to release container";
        }
      });
      return false;
    } finally {
      runInAction(() => {
        this.actioningIds = this.actioningIds.filter((id) => id !== instance.id);
      });
    }
  }

  /**
   * Back to the constructed state, so nothing of the person who signed out
   * is left for the next one. Called by RootStore.purgeUserData on sign-out.
   */
  reset() {
    this.loading = false;
    this.error = null;
    this.actioningIds = [];
    this.searchQuery = "";
    this.statusFilter = "all";
    this.sortKey = "containerName";
    this.sortDir = "asc";
  }
}

export default InternalContainersStore;
